import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { AccidentsTable } from '../../../../components/display/tables/AccidentsTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
// Styles
import { Container, ContainerButton } from '../../../main/workOrders/components/MainStyledComponents';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { dataTypes } from '../../../../components/display/tables/AccidentsTable/types';

// Utils
import { modalIds } from '../../../../components/modals/SuperModal/types';
import { openModal } from '../../../../state/actions/ui/modal';

// Hooks
import { usePersistentSelection } from '../../../../hooks/usePersistentSelection';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_ACCIDENTS } from '../../../../gql/queries/accidents/accidents';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';

export default function HistoricalAccidentsReportPage(): JSX.Element {
  const { t } = useTranslation(['pages', 'tables']);
  const dispatch = useDispatch();
  const data = useAsyncValue() as dataTypes;
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  // Persistent selection across pages (controlled checkbox mode)
  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: data.accidents || [],
    totalCount: data.totalCount || 0,
  });

  const onReFetchData = useCallback(async () => {
    const currentCursor = getCursor(tableIds.REPORTING_ACCIDENTS)(
      store.getState()
    );
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    // Espejo de HistoricalActionsReport — sin esto el refetch pedía siempre
    // takeBase (25) aunque el selector estuviera en 50/100.
    const effectiveTake = firstPageTake(currentCursor?.take, takeBase);
    await client.query({
      query: FETCH_ACCIDENTS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: currentCursor?.concessionId,
          weatherId: currentCursor?.weatherId,
          mediumKnowledgeId: currentCursor?.mediumKnowledgeId,
          orderBy: currentCursor?.orderBy ?? 'desc',
          sortField: currentCursor?.sortField,
          toDate: currentCursor?.toDate,
          fromDate: currentCursor?.fromDate,
          indexType: currentCursor?.indexType,
          indexExpedition: currentCursor?.indexExpedition,
          classification: currentCursor?.classification,
          capitolId: currentCursor?.capitolId,
          subCapitolId: currentCursor?.subCapitolId,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          states: currentCursor?.states,
          userIds: currentCursor?.userIds,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });

    // setCursorData OMITE sortField/orderBy → el merge de cursorTables los preserva
    dispatch(
      setCursor(tableIds.REPORTING_ACCIDENTS, {
        take: effectiveTake,
        cursor: '',
        concessionId: currentCursor?.concessionId,
        weatherId: currentCursor?.weatherId,
        mediumKnowledgeId: currentCursor?.mediumKnowledgeId,
        toDate: currentCursor?.toDate,
        fromDate: currentCursor?.fromDate,
        indexType: currentCursor?.indexType,
        indexExpedition: currentCursor?.indexExpedition,
        classification: currentCursor?.classification,
        capitolId: currentCursor?.capitolId,
        subCapitolId: currentCursor?.subCapitolId,
        predefinedOrderId: currentCursor?.predefinedOrderId,
        states: currentCursor?.states,
        userIds: currentCursor?.userIds,
        pkInitKm: currentCursor?.pkInitKm,
        pkInitMeter: currentCursor?.pkInitMeter,
        pkEndKm: currentCursor?.pkEndKm,
        pkEndMeter: currentCursor?.pkEndMeter,
        onlyFlagged: currentCursor?.onlyFlagged,
      })
    );

    revalidator.revalidate();
  }, []);

  const handleDownloadPDF = useCallback(() => {
    const currentCursor = getCursor(tableIds.REPORTING_ACCIDENTS)(store.getState());
    // Extract only valid filter fields (exclude Redux metadata like id, take, cursor)
    const { id: _id, take: _take, cursor: _cursor, ...filterFields } = currentCursor || {} as any;
    dispatch(
      openModal(modalIds.DOWNLOAD_HISTORIC, {
        type: 'Accident',
        filterPositionColumn: 1,
        selectedRows: selectAllMode ? [] : selectedRows,
        showReport: true,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: filterFields,
        totalCount: data.totalCount || 0,
      })
    );
  }, [selectedRows, dispatch, selectAllMode, excludedIds, data.totalCount]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, [revalidator]);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableIds.REPORTING_ACCIDENTS)(store.getState());
    const effectiveTake = firstPageTake(currentCursor?.take, takeBase);
    await client.query({
      query: FETCH_ACCIDENTS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: currentCursor?.concessionId,
          weatherId: currentCursor?.weatherId,
          mediumKnowledgeId: currentCursor?.mediumKnowledgeId,
          orderBy: currentCursor?.orderBy ?? 'desc',
          sortField: currentCursor?.sortField,
          toDate: currentCursor?.toDate,
          fromDate: currentCursor?.fromDate,
          indexType: currentCursor?.indexType,
          indexExpedition: currentCursor?.indexExpedition,
          classification: currentCursor?.classification,
          capitolId: currentCursor?.capitolId,
          subCapitolId: currentCursor?.subCapitolId,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          states: currentCursor?.states,
          userIds: currentCursor?.userIds,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    // NO despachar setCursor — el usuario sigue donde está
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);

  return (
    <Container>
      <div id="historical-Accident">
        <AccidentsTable
          titleText={t('pages:accidents.filterBy')}
          onReFetchData={onReFetchData}
          onReFetchPagination={onRefetchPagination}
          tableId={tableIds.REPORTING_ACCIDENTS}
          data={data}
          selectionMode="checkbox"
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          selectAllMode={selectAllMode}
          excludedIds={excludedIds}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          selectedCount={selectedCount}
          // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas».
          showFlagColumn
          showOnlyFlaggedFilter
        />
      </div>
      <ContainerButton>
        <MainButton
          text={t('pages:actions.downloadHistoric')}
          disabled={selectedCount === 0}
          onClick={handleDownloadPDF}
        />
      </ContainerButton>
    </Container>
  );
}
